// Global Instructions Rule Applied!
// Shape of GET /investor-statements/runs/:id/send-plan (stmt__campaign_plan):
// what "Send statements" on a statement run would create - every fund of the
// run with whether it can be sent, per investor account its attachments (one
// merged PDF per fund where the fund merges on send, else the stored PDFs)
// packed into the PARTS it needs under the mailbox cap (docs/QUARTERLY_
// REPORT_DISTRIBUTION_DESIGN.md d.3: an account over the cap is sent in
// several emails), its recipients on the recipients topic and the merge
// fields of each of its emails - and what blocks the send. The backend twin
// is investor_statement_send_plan_record.py; both are strict.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

/** What a document of a send is: a capital account statement, the fund's
 *  financial statements (the pack), the fund report or the account's
 *  investor performance deck (a run-level document, once per account;
 *  docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1, PR 4). */
export const SendPlanKind = {
  statement: 'statement',
  pack: 'pack',
  report: 'report',
  accountDocument: 'account_document'
} as const

export const SendPlanStatementSchema = z
  .object({
    // a statement's id and file, or the performance deck's; one pair is set
    investor_statements__id: z.string().nullish(),
    investor_statement_account_documents__id: z.string().nullish(),
    investor_statement_types__code: z.string(),
    investor_statement_types__short_label: z.string(),
    // the document group of its type (reports / statements)
    statement_document_groups__code: z.string().nullish(),
    prog__kind: z.enum(['statement', 'pack', 'report', 'account_document']),
    // entity = the fund's financial statements or the fund report; account
    // = the deck; else whose capital account
    prog__scope: z.enum(['entity', 'commitment', 'subscription', 'pocket', 'account']),
    prog__member_name: z.string().nullish(),
    investors__name: z.string().nullish(),
    investor_statements__version: z.number(),
    file_urls_investor_statements__id: z.string().nullish(),
    file_urls_investor_statement_account_documents__id: z.string().nullish(),
    // the stored file's name and size, whichever table holds it
    file_urls_investor_statements__file_name: z.string(),
    file_urls_investor_statements__file_size_bytes: z.number().nullish(),
    prog__sent_at: z.string().nullish()
  })
  .strict()

/** One attachment of an account: a stored PDF as it is, or the merged PDF of
 *  the account's statements for a fund with the fund's financial statements;
 *  the performance deck is a run-level file with no fund. */
export const SendPlanFileSchema = z
  .object({
    investor_statement_run_funds__id: z.string().nullish(),
    prog__fund_name: z.string().nullish(),
    prog__is_merged: z.boolean(),
    prog__file_name: z.string(),
    prog__bytes: z.number(),
    prog__sent_at: z.string().nullish(),
    // the part (email) of the account this file goes in; null for a file
    // that fits no message at all (the account is then over the cap)
    prog__part_no: z.number().nullish(),
    statements: z.array(SendPlanStatementSchema)
  })
  .strict()

/** The merge fields of one email of an account, formatted on the server as
 *  the statements print them: fund_names, investor_names and
 *  statement_count are those of the part; part_suffix reads " (1 of 3)" for
 *  a split account and "" for everyone else; other_parts_note names the
 *  funds whose documents are in the account's other emails ("" for an
 *  account with one). */
export const SendPlanMergeDataSchema = z
  .object({
    run_name: z.string(),
    period_label: z.string(),
    period_end: z.string(),
    statement_date: z.string(),
    fund_names: z.string(),
    investor_names: z.string(),
    statement_count: z.number(),
    part_no: z.number(),
    part_count: z.number(),
    part_suffix: z.string(),
    part_fund_names: z.string(),
    other_parts_note: z.string()
  })
  .strict()

/** One email of an account: its files' count and size, its funds, and the
 *  merge fields of that email. */
export const SendPlanPartSchema = z
  .object({
    part_no: z.number(),
    prog__bytes: z.number(),
    prog__file_count: z.number(),
    prog__fund_names: z.string(),
    merge_data: SendPlanMergeDataSchema
  })
  .strict()

export const SendPlanRecipientSchema = z
  .object({
    contacts__id: z.string(),
    contacts__first_name: z.string().nullish(),
    contacts__last_name: z.string().nullish(),
    contacts__email: z.string(),
    role: z.string()
  })
  .strict()

export const SendPlanAccountSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string().nullish(),
    // the size the mailbox send is measured on: the largest part's files
    // plus the body
    prog__bytes: z.number(),
    // a file of the account fits no message at all: it cannot be sent by
    // email (a merged PDF over the cap; switch the merge off)
    prog__over_cap: z.boolean(),
    // part 1's merge fields
    merge_data: SendPlanMergeDataSchema,
    files: z.array(SendPlanFileSchema),
    // the emails the account receives, in order; one for most accounts
    parts: z.array(SendPlanPartSchema),
    recipients: z.array(SendPlanRecipientSchema),
    // at least one To recipient: without one the account gets no email
    prog__has_to: z.boolean()
  })
  .strict()

export const SendPlanFundSchema = z
  .object({
    investor_statement_run_funds__id: z.string(),
    prog__name: z.string(),
    prog__kind: z.enum(['fund', 'fund_group']),
    // named in the request (or all) and sendable
    prog__selected: z.boolean(),
    // why the fund cannot be sent; null when it can
    prog__send_blocked_reason: z.string().nullish(),
    fund_statement_settings__merge_on_send: z.boolean(),
    // its attachments over every account, and how many were already sent
    prog__file_count: z.number(),
    prog__sent_file_count: z.number()
  })
  .strict()

export const InvestorStatementSendPlanSchema = z
  .object({
    run: z
      .object({
        investor_statement_runs__id: z.string(),
        investor_statement_runs__name: z.string(),
        investor_statement_runs__period_end: z.string(),
        prog__period_label: z.string(),
        investor_statement_runs__statement_date: z.string()
      })
      .strict(),
    // the document groups this plan carries (reports, statements;
    // docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1) and their labels
    // joined ('Fund reports and Statements')
    document_group_codes: z.array(z.string()),
    prog__document_group_label: z.string(),
    comms_topic_id: z.string().nullish(),
    email_design_template_id: z.string().nullish(),
    max_message_bytes: z.number(),
    // why nothing can be sent, as a sentence; null when something can
    prog__blocked_reason: z.string().nullish(),
    funds: z.array(SendPlanFundSchema),
    accounts: z.array(SendPlanAccountSchema),
    prog__summary: z
      .object({
        funds: z.number(),
        accounts: z.number(),
        files: z.number(),
        accounts_without_recipient: z.number(),
        accounts_over_cap: z.number(),
        // files already sent, whatever only_unsent says
        already_sent: z.number(),
        // statement email sets the run already has (a split set counts once)
        campaigns: z.number(),
        // the most parts any account needs: how many drafts Create emails
        // makes; and how many accounts need more than one
        part_count: z.number(),
        accounts_split: z.number()
      })
      .strict()
  })
  .strict()

export type SendPlanAccount = z.infer<typeof SendPlanAccountSchema>
export type SendPlanPart = z.infer<typeof SendPlanPartSchema>
export type SendPlanFund = z.infer<typeof SendPlanFundSchema>
export type InvestorStatementSendPlan = z.infer<typeof InvestorStatementSendPlanSchema>

export function validateInvestorStatementSendPlan(data: unknown): InvestorStatementSendPlan {
  try {
    return validateWithZodSchema(InvestorStatementSendPlanSchema, data, 'investor statement send plan')
  } catch (error) {
    throw new Error(`Investor statement send plan validation failed: ${error}`)
  }
}
